import { useEffect, useRef, useState, type CSSProperties } from "react";
import { cardBack, cardFront, NUMGNUM_NAMES, type Edition } from "../lib/cards";

type Props = {
  level: number;
  edition: Edition;
  founder?: boolean;
  shiny?: boolean;
  serial?: number;
  founderName?: string;
  className?: string;
  style?: CSSProperties;
  interactive?: boolean;
  initialFlipped?: boolean;
  showsSerialBadge?: boolean;
};

export function NumgnumCard({
  level,
  edition,
  founder = false,
  shiny = false,
  serial = 1,
  founderName = "FOUNDING FORGER",
  className = "",
  style,
  interactive = true,
  initialFlipped = false,
  showsSerialBadge = false,
}: Props) {
  const [flipped, setFlipped] = useState(initialFlipped);
  const [flipping, setFlipping] = useState(false);
  const timers = useRef<number[]>([]);
  const title = `${NUMGNUM_NAMES[level] ?? `NUMGNUM ${level}`} · ${edition}`;

  useEffect(() => () => timers.current.forEach(window.clearTimeout), []);

  function flipCard() {
    if (!interactive || flipping) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setFlipped((value) => !value);
      return;
    }
    setFlipping(true);
    timers.current.push(window.setTimeout(() => setFlipped((value) => !value), 180));
    timers.current.push(window.setTimeout(() => setFlipping(false), 380));
  }

  return (
    <button
      type="button"
      className={`numgnum-card ${flipped ? "is-flipped" : ""} ${flipping ? "is-flipping" : ""} ${founder ? "is-founder" : ""} ${shiny ? "is-shiny" : ""} ${className}`}
      style={style}
      onClick={flipCard}
      aria-label={`${flipped ? "Show front of" : "Show back of"} ${title}`}
    >
      <span className="numgnum-card__inner" data-face={flipped ? "back" : "front"}>
        {!flipped ? (
          <span className="numgnum-card__face numgnum-card__front">
            <img src={cardFront(level, edition, founder)} alt={`${title} card front`} loading="lazy" decoding="async" />
            {(founder || shiny) && <span className="numgnum-card__gleam" aria-hidden="true" />}
            {shiny && <span className="numgnum-card__sparkles" aria-hidden="true" />}
          </span>
        ) : (
          <span className="numgnum-card__face numgnum-card__back">
            <img src={cardBack(edition, founder)} alt={`${title} card back`} loading="lazy" decoding="async" />
            {founder && (
              <span className="founder-meta">
                <strong>SERIAL NO. {serial}</strong>
                <strong>FOUNDER: {founderName.toUpperCase()}</strong>
              </span>
            )}
            <span className="numgnum-card__gleam" aria-hidden="true" />
          </span>
        )}
      </span>
      {showsSerialBadge && !flipped && (
        <span className="numgnum-card__serial-badge" aria-label={`Serial number ${serial}`}>
          NO. {serial}
        </span>
      )}
    </button>
  );
}
